import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CSVLink } from 'react-csv';
import TotalIvGenerationChart from './TotalIvGenerationChart';
import './IvMeasurementTotalList.css';
import { format } from 'date-fns';
import config from '../../../config';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEv_list } from '../../../store';
import useIsMobile from '../../../hooks/useIsMobile';
import { processEvSensorData } from '../../../utils/parseEvSensorData ';

// 시간 범위 내에 데이터가 있는지 확인하는 함수 (변환 없이 그대로 사용)
const isWithinTimeRange = (createdAt, targetTime) => {
  const [targetHours, targetMinutes] = targetTime.split(':').map(Number);
  const dateObj = new Date(createdAt);

  const itemHours = dateObj.getUTCHours();
  const itemMinutes = dateObj.getUTCMinutes();

  const startTime = targetHours * 60 + targetMinutes;
  const endTime = startTime + 10;
  const itemTimeMinutes = itemHours * 60 + itemMinutes;

  return itemTimeMinutes >= startTime && itemTimeMinutes < endTime;
};

// 가장 가까운 데이터를 찾고, 허용 시간 범위를 초과하면 null 반환
function findClosestTimeData(dataArray, targetLabel, maxDiffMinutes = 10) {
  if (!dataArray || dataArray.length === 0) return null;

  let closest = null;
  let minDiff = Infinity;

  dataArray.forEach(item => {
    const date = new Date(item.created_at);
    date.setHours(date.getHours());

    const itemLabel = date.toTimeString().substring(0, 5); // "HH:MM"

    const diff = Math.abs(labelToMinutes(itemLabel) - labelToMinutes(targetLabel));
    if (diff <= maxDiffMinutes && diff < minDiff) {
      minDiff = diff;
      closest = item;
    }
  });

  return closest;
}

// HH:MM 문자열을 분 단위로 변환
function labelToMinutes(label) {
  const [h, m] = label.split(':').map(Number);
  return h * 60 + m;
}

function IvMeasurementTotalList() {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const Ev_list = useSelector((state) => state.Ev_list || {});
  const [chartData, setChartData] = useState(new Array(90).fill(0));
  const [tooltipData, setTooltipData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isEvReady, setIsEvReady] = useState(false);
  const [isEvDataReady, setIsEvDataReady] = useState(false);

  // ⬇ csvData 선언은 여기 1번만 유지
  const [csvData, setCsvData] = useState([]);
  const [startDate, setStartDate] = useState(new Date());

  // 중복 실행 방지/요청 취소/타이머 라벨용 ref
  const fetchingRef = useRef(false);
  const abortRef = useRef(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    dispatch(fetchEv_list());
  }, [dispatch]);

  // 언마운트 시 진행 중 요청 취소
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const labels  = useMemo(() => [
      '05:00', '05:10', '05:20', '05:30', '05:40', '05:50',
      '06:00', '06:10', '06:20', '06:30', '06:40', '06:50',
      '07:00', '07:10', '07:20', '07:30', '07:40', '07:50',
      '08:00', '08:10', '08:20', '08:30', '08:40', '08:50',
      '09:00', '09:10', '09:20', '09:30', '09:40', '09:50',
      '10:00', '10:10', '10:20', '10:30', '10:40', '10:50',
      '11:00', '11:10', '11:20', '11:30', '11:40', '11:50',
      '12:00', '12:10', '12:20', '12:30', '12:40', '12:50',
      '13:00', '13:10', '13:20', '13:30', '13:40', '13:50',
      '14:00', '14:10', '14:20', '14:30', '14:40', '14:50',
      '15:00', '15:10', '15:20', '15:30', '15:40', '15:50',
      '16:00', '16:10', '16:20', '16:30', '16:40', '16:50',
      '17:00', '17:10', '17:20', '17:30', '17:40', '17:50',
      '18:00', '18:10', '18:20', '18:30', '18:40', '18:50',
      '19:00', '19:10', '19:20', '19:30', '19:40', '19:50',
      '20:00'
    ], []);

  const fetchData = useCallback(async () => {
    // 겹침 실행 방지
    if (fetchingRef.current) {
      console.warn('[fetchData] in-flight → skip');
      return;
    }
    fetchingRef.current = true;

    // 이전 요청 취소
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // 고유 타이머 라벨
    const id = ++runIdRef.current;
    const T = (name) => `${name}#${id}`;

    console.time(T('0.total'));

    try {
      // 선택된 날짜를 기준으로 KST 시간대를 고려한 문자열 생성
      const selectedDateInKST = new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];

      const processSeqData = (data) => {
        const seqMap = {};
        data.forEach(item => {
          if (!seqMap[item.seq]) {
            seqMap[item.seq] = [];
          }
          seqMap[item.seq].push(item);
        });
        return seqMap;
      };

      // 첫 번째 요청: get_hourly_generation
      console.time(T('1.API gen'));
      const genRes = await fetch(`${config.backendUrl}/get_hourly_generation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDateInKST }),
        signal: controller.signal,
      });
      console.timeEnd(T('1.API gen'));
      // console.log(`[gen#${id}] status:`, genRes.status, 'ct:', genRes.headers.get('content-type'), 'len:', genRes.headers.get('content-length'));

      console.time(T('2.parse gen'));
      const genData = await genRes.json();
      console.timeEnd(T('2.parse gen'));

      // 두 번째 요청: get_hourly_Ev_mrt_data
      console.time(T('1.API env'));
      const envRes = await fetch(`${config.backendUrl}/get_hourly_Ev_mrt_data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDateInKST }),
        signal: controller.signal,
      });
      console.timeEnd(T('1.API env'));
      // console.log(`[env#${id}] status:`, envRes.status, 'ct:', envRes.headers.get('content-type'), 'len:', envRes.headers.get('content-length'));

      console.time(T('2.parse env'));
      const envData = await envRes.json();
      console.timeEnd(T('2.parse env'));

      console.time(T('3.compute processSeqData'));
      const seqData = processSeqData(envData.data);
      console.timeEnd(T('3.compute processSeqData'));

      // 현재전력합산값 계산
      const gridPowerByTime = {};

      console.time(T('3.compute gridPowerByTime'));
      genData.data.forEach(item => {
        const timeStr = new Date(item.created_at).toISOString().split('T')[1].slice(0, 5);
        if (!gridPowerByTime[timeStr]) {
          gridPowerByTime[timeStr] = 0;
        }
        gridPowerByTime[timeStr] += item.Grid_Power || 0;
      });
      console.timeEnd(T('3.compute gridPowerByTime'));

      // 환경센서
      const horizontalIrradiance = new Array(labels.length).fill(null);
      const tiltedIrradiance = new Array(labels.length).fill(null);
      const surfaceTemp = new Array(labels.length).fill(null);
      const outdoorTemp = new Array(labels.length).fill(null);

      const seqs = Ev_list.seq || [];
      const sunTypes = Ev_list.ev_sunType || [];
      const tempTypes = Ev_list.ev_temperatureType || [];

      console.time(T('3.compute fill env series'));
      labels.forEach((label, labelIndex) => {
        seqs.forEach((seq, index) => {
          const closest = findClosestTimeData(seqData[seq], label);
          if (!closest) return;

          const sunType = sunTypes[index];
          const tempType = tempTypes[index];

          if (sunType === 0 && horizontalIrradiance[labelIndex] === null) {
            horizontalIrradiance[labelIndex] = closest.sun;
          }
          if (sunType === 1 && tiltedIrradiance[labelIndex] === null) {
            tiltedIrradiance[labelIndex] = closest.sun;
          }
          if (tempType === 0 && surfaceTemp[labelIndex] === null) {
            surfaceTemp[labelIndex] = closest.temperature / 10;
          }
          if (tempType === 1 && outdoorTemp[labelIndex] === null) {
            outdoorTemp[labelIndex] = closest.temperature / 10;
          }
        });
      });
      console.timeEnd(T('3.compute fill env series'));

      console.time(T('3.compute build chartData'));
      const chartDataByTime = [];
      const tooltipDataByTime = [];

      labels.forEach((label, idx) => {
        const dataPoint = {
          time: label,
          gridPower: gridPowerByTime[label] || 0,
          horizontalIrradiance: horizontalIrradiance[idx] ?? null,
          tiltedIrradiance: tiltedIrradiance[idx] ?? null,
          surfaceTemp: surfaceTemp[idx] ?? null,
          outdoorTemp: outdoorTemp[idx] ?? null,
        };

        chartDataByTime.push(dataPoint);
        tooltipDataByTime.push(dataPoint);
      });
      console.timeEnd(T('3.compute build chartData'));

      setChartData(chartDataByTime);
      setTooltipData(tooltipDataByTime);

      // const evDataReady =
      //   horizontalIrradiance.some(val => val !== null) ||
      //   tiltedIrradiance.some(val => val !== null) ||
      //   surfaceTemp.some(val => val !== null) ||
      //   outdoorTemp.some(val => val !== null);

      // setIsEvDataReady(evDataReady);

      setIsEvDataReady(true);

      // 페인트 타이밍(대략)
      const perf_t1 = performance.now();
      requestAnimationFrame(() => {
        const perf_t2 = performance.now();
        // console.log(`[diag#${id}] paint ~ ${(perf_t2 - perf_t1).toFixed(1)} ms after setState`);
      });

      console.timeEnd(T('0.total'));
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.warn('[fetchData] aborted');
      } else {
        console.error('Error fetching data:', error);
      }
      console.timeEnd(T('0.total'));
    } finally {
      fetchingRef.current = false;
    }
  }, [labels, selectedDate, Ev_list]);

  useEffect(() => {
    setSelectedDate(startDate);
  }, [startDate]);

  useEffect(() => {
    if (Ev_list.seq && Ev_list.seq.length > 0) {
      setIsEvReady(true);
    }
  }, [Ev_list]);

  useEffect(() => {
    if (!isEvReady) return;

    fetchData(); // Ev_list 준비된 뒤에만 실행
    const interval = setInterval(fetchData, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchData, isEvReady]);

  const handleCsvGenerate = () => {
    const baseDateStr = format(startDate, 'yyyy-MM-dd');
    const csvFormattedData = chartData.map(item => {
      return {
        시간: `${baseDateStr} ${item.time}`,
        '발전량 (kW)': (item.gridPower || 0),
        '수평 일사량 (W/m²)': item.horizontalIrradiance ?? 0,
        '경사 일사량 (W/m²)': item.tiltedIrradiance ?? 0,
        '외기 온도 (℃)': item.outdoorTemp ?? 0,
        '표면 온도 (℃)': item.surfaceTemp ?? 0
      };
    });

    setCsvData(csvFormattedData);
  };

  return (
    <div className="total-wrapper">
      <div className='inverterGenInfo-TopBox'>
        <div style={{ color: '#6B7476', fontSize: isMobile? '17px':'20px', fontWeight: '600', paddingLeft: '20px' }}>◎전체 센서 값 보기 </div>
        <div style={{ flexGrow: '1' }}></div>
        <div style={{ color: '#6B7476', fontSize: isMobile? '17px':'20px', fontWeight: '600', paddingRight: '20px' }}>⦁ ⦁ ⦁</div>
      </div>
      <div className="top-controls">
        <input
          type="date"
          className="date-picker"
          value={format(startDate, 'yyyy-MM-dd')}
          onChange={(e) => setStartDate(new Date(e.target.value))}
        />
        <CSVLink
          data={csvData}
          filename={`발전데이터_${format(startDate, 'yyyy-MM-dd')}.csv`}
          className="csv-button"
          onClick={handleCsvGenerate}
        >
          CSV 다운로드
        </CSVLink>
      </div>

      {chartData.length > 0 ? (   // ★ chartData만 있으면 우선 렌더
        <TotalIvGenerationChart chartData={chartData} />
      ) : (
        <div className="loader"></div>
      )}

    </div>
  )
};

export default IvMeasurementTotalList;
